import { ConfigurableFocusTrapFactory } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
	ViewContainerRef,
} from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { fromRxValue } from "@app/_helpers/utils";
import { SatPopoverComponent as SatPopover, SatPopoverAnchoringService } from "@ncstate/sat-popover";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { UserSettings, UserSettingsQuery } from "@timeghost/web-api";
import { xor } from "lodash-es";
import { BehaviorSubject, combineLatest } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { firstBy } from "thenby";
export const COMEGO_ICON_MAP = {
	work: "work_history",
	pause: "pause",
	absence: "beach_access",
};
@UntilDestroy()
@Component({
	selector: "tg-comego-type-filter-popover",
	templateUrl: "./comego-type-filter-popover.component.html",
	styleUrls: ["./comego-type-filter-popover.component.scss"],
	providers: [{ provide: SatPopoverAnchoringService, useExisting: false }],
})
export class ComegoTypeFilterPopoverComponent extends SatPopover implements OnInit {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		_focusTrapFactory: ConfigurableFocusTrapFactory,
		_anchoringService: SatPopoverAnchoringService,
		_viewContainerRef: ViewContainerRef,
		@Optional() @Inject(DOCUMENT) _document: any,
		private cdr: ChangeDetectorRef,
		private translate: TranslateService
	) {
		super(
			_focusTrapFactory,
			_anchoringService,
			_viewContainerRef,
			"200ms cubic-bezier(0.25, 0.8, 0.25, 1)",
			_document
		);
	}

	search = new UntypedFormControl("", [Validators.required, Validators.minLength(2)]);
	private _selectedItems = new BehaviorSubject<string[]>([]);
	readonly selectedItems$ = this._selectedItems.asObservable().pipe(distinctUntilChanged());

	filteredComegoTypes: any[] = [];

	get selectedItems() {
		return this._selectedItems.getValue();
	}
	readonly iconMap = COMEGO_ICON_MAP;

	@Input()
	set selectedItems(val: string[]) {
		const prev = this.selectedItems;
		this._selectedItems.next(val);

		if (xor(val, prev).length > 0) this.selectedItemsChange.emit(val);
	}

	@Output() selectedItemsChange = new EventEmitter(true);

	items$filtered = fromRxValue(
		combineLatest([
			this.search.valueChanges.pipe(startWith(this.search.value)),
			this.userSettingsQuery.select(),
			this.selectedItems$,
		]).pipe(
			map(([q, user, selected]: [string, UserSettings, string[]]) => {
				const filteredComegoTypes = (() => {
					let comegoTypes: { name: string; type: string; typeId: string; useTranslate: boolean }[] = [
						{ name: "work", type: "work", typeId: "work", useTranslate: true },
						// { name: "pause", type: "pause", typeId: "pause", useTranslate: true },
						{ name: "absence", type: "absence", typeId: "absence", useTranslate: true },
					];
					//@ts-ignore
					const subcategories =
						user.workspace?.["workinghours"]?.absenceSubcategories.map((x: any) => {
							return { name: x.title, type: "absence", typeId: x.id, useTranslate: false };
						}) ?? [];
					comegoTypes = [...comegoTypes, ...subcategories];

					if (this.search.invalid) return comegoTypes || [];
					return comegoTypes.filter((x) => {
						return x.name.toLowerCase().indexOf(q.toLowerCase()) !== -1;
					});
				})()
					.map((x) => {
						return {
							...x,
							selected: selected.findIndex((u) => u === x.typeId) !== -1,
						};
					})
					.sort(
						firstBy((x: { typeId: string }) => {
							if (x.typeId === "work") return 0;
							if (x.typeId === "absence") return 1;
							return 2;
						}).thenBy("selected", "desc")
					);
				this.filteredComegoTypes = filteredComegoTypes;
				return filteredComegoTypes;
			})
		)
	);

	toggle() {
		this._open ? this.close() : this.open();
	}

	@Output() itemsClose = new EventEmitter(true);

	ngOnInit(): void {
		const user = this.userSettingsQuery.getValue();
		this.openAnimationStartAtScale = this.closeAnimationEndAtScale = 0.9375;
		this.hasBackdrop = true;
		this.yAlign = "below";
		this.xAlign = "center";
		this.closed.asObservable().subscribe(() => this.search.reset(""));
		this.closed
			.asObservable()
			.pipe(
				untilDestroyed(this),
				map(() => this.selectedItems),
				distinctUntilChanged()
			)
			.subscribe((val) => this.itemsClose.emit(val));
	}

	trackId(index: number, { id }: { id: string }) {
		return id;
	}

	selectItem(id: string) {
		if (id === "absence") {
			if (this.selectedItems.includes(id)) this.unselectAllAbsences();
			else this.selectAllAbsences();
		} else if (this.selectedItems.includes(id)) {
			this.selectedItems = this.selectedItems.filter((x) => x !== id);
			if (id !== "work" && id !== "absence")
				this.selectedItems = this.selectedItems.filter((x) => x !== "absence");
		} else this.selectedItems = [...this.selectedItems, id];
	}

	selectAllAbsences() {
		const absences = this.filteredComegoTypes
			.filter((a) => {
				return a.type === "absence";
			})
			.map((a) => {
				return a.typeId;
			});
		this.selectedItems = this.selectedItems.filter((item) => !absences.includes(item));
		this.selectedItems = [...this.selectedItems, ...absences];
	}

	unselectAllAbsences() {
		this.selectedItems = this.selectedItems.filter((item) => item === "work" || item === "pause");
	}

	selectNone() {
		this.selectedItems = [];
	}

	close() {
		super.close(this.selectedItems);
	}
}
